import React from "react";

/** Hidden row keys for sorting (not table columns). Use numeric 0/1 for booleans — RowsData typing. */
export const TABLE_SORT_META = {
  createdDateIso: "__sortCreatedDate",
  modifiedDateIso: "__sortModifiedDate",
  enabled: "__sortEnabled",
  isActive: "__sortIsActive",
} as const;

export type TableSortValue = string | number | null | undefined;
export type TableSortDirection = "asc" | "desc";

export interface TableSortDefault<TField extends string = string> {
  sortField: TField;
  sortDirection: TableSortDirection;
}

export interface TableSortColumn<TField extends string = string> {
  field: TField;
  label: React.ReactNode;
  /** When set to the same string as an entry in `headers[]`, that column header shows service-catalog style sorting. */
  headerKey?: string;
  getSortValue: (row: RowsData) => TableSortValue;
}

export interface TableSortConfig<TField extends string = string> {
  storageKey: string;
  defaultSort: TableSortDefault<TField>;
  columns: readonly TableSortColumn<TField>[];
}

export function getSortableTextValue(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  return "";
}

export function compareTableSortValues(
  a: TableSortValue,
  b: TableSortValue
): number {
  const normalizedA = a ?? "";
  const normalizedB = b ?? "";
  if (typeof normalizedA === "number" && typeof normalizedB === "number") {
    return normalizedA - normalizedB;
  }
  const textA = String(normalizedA).trim();
  const textB = String(normalizedB).trim();
  const isIsoLikeDate = (value: string) =>
    /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(value);
  const parsedDateA = Date.parse(textA);
  const parsedDateB = Date.parse(textB);
  if (
    isIsoLikeDate(textA) &&
    isIsoLikeDate(textB) &&
    !Number.isNaN(parsedDateA) &&
    !Number.isNaN(parsedDateB)
  ) {
    return parsedDateA - parsedDateB;
  }
  return textA.localeCompare(textB, undefined, { sensitivity: "base" });
}

export function buildRowsDataCompare<TField extends string>(
  columns: readonly TableSortColumn<TField>[]
) {
  const byField = new Map(columns.map((c) => [c.field, c.getSortValue] as const));
  return (a: RowsData, b: RowsData, field: TField): number => {
    const getVal = byField.get(field);
    if (!getVal) return 0;
    return compareTableSortValues(getVal(a), getVal(b));
  };
}
